import PropTypes from "prop-types";
import React, { useState, useMemo } from "react";

const SpellingOnboarding = ({
  language,
  languageCode,
  languageInfo,
  wordPacks,
  publicLists,
  maxWords,
  languages,
  csrfToken,
  createPath,
  browseListsPath,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedPack, setExpandedPack] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasPacks = wordPacks && wordPacks.length > 0;

  const currentLangInfo = useMemo(() => {
    const found = languages.find((l) => l.id === selectedLanguage);
    return (
      found || {
        label: languageInfo.name,
        flag: languageInfo.flag,
        code: languageCode,
      }
    );
  }, [selectedLanguage, languages, languageInfo, languageCode]);

  const toggleWord = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < maxWords) {
        next.add(id);
      }
      return next;
    });
  };

  const togglePack = (packId) => {
    setExpandedPack((prev) => (prev === packId ? null : packId));
  };

  const selectAllFromPack = (pack) => {
    const available = maxWords - selectedIds.size;
    if (available <= 0) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      const packWordIds = pack.baseWords.map((bw) => bw.id);
      for (const id of packWordIds) {
        if (next.size >= maxWords) break;
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = (withWords = true) => {
    if (isSubmitting) return;
    if (withWords && selectedIds.size === 0) return;
    setIsSubmitting(true);

    const form = document.createElement("form");
    form.method = "POST";
    form.action = createPath;

    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "authenticity_token";
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    const langInput = document.createElement("input");
    langInput.type = "hidden";
    langInput.name = "language";
    langInput.value = selectedLanguage;
    form.appendChild(langInput);

    if (withWords) {
      for (const id of selectedIds) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "base_word_ids[]";
        input.value = id;
        form.appendChild(input);
      }
    }

    document.body.appendChild(form);
    form.submit();
  };

  const categoryIcons = {
    animals: "🐾",
    food: "🍎",
    home: "🏠",
    school: "📚",
    nature: "🌿",
    body: "🫀",
    colors: "🎨",
    actions: "🏃",
  };

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          <span className="text-4xl mr-2">{currentLangInfo.flag}</span>
          Let's set up your {currentLangInfo.label} word list!
        </h1>
        <p className="text-gray-600 text-lg">
          {hasPacks
            ? "Pick words from the packs below, or create an empty list and add your own."
            : "Create your word list and start adding words — type in English and we'll translate for you!"}
        </p>
      </div>

      {/* Language Picker */}
      <div className="mb-6 flex justify-center">
        <div className="inline-flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">
            Language:
          </label>
          <select
            value={selectedLanguage}
            onChange={(e) => {
              setSelectedLanguage(e.target.value);
              setSelectedIds(new Set());
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {languages
              .filter((l) => l.id !== "english")
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {l.flag} {l.label}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Quick Start — always visible, primary CTA when no packs */}
      <div
        className={`mb-8 text-center ${hasPacks ? "bg-gray-50 border border-gray-200" : "bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200"} rounded-xl p-6`}
      >
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {hasPacks ? "Or start from scratch" : "Get started in seconds"}
        </h2>
        <p className="text-gray-600 text-sm mb-4">
          Create your {currentLangInfo.label} word list and add words one by
          one. You can type words in English and we'll translate them for you!
        </p>
        <button
          onClick={() => handleSubmit(false)}
          disabled={isSubmitting}
          className={`px-8 py-3 rounded-full font-bold text-lg shadow-lg transition-all ${
            isSubmitting
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : hasPacks
                ? "bg-white text-indigo-600 border-2 border-indigo-600 hover:bg-indigo-50 hover:shadow-xl"
                : "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-xl hover:scale-105"
          }`}
        >
          {isSubmitting
            ? "Creating..."
            : `Create My ${currentLangInfo.label} Word List`}
        </button>
      </div>

      {/* Word Packs — only when they exist */}
      {hasPacks && (
        <>
          {/* Selection Counter */}
          <div className="mb-4 text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Or pick starter words
            </h2>
            <div className="inline-flex items-center px-4 py-2 bg-indigo-50 rounded-full">
              <span
                className={`text-sm font-semibold ${selectedIds.size >= maxWords ? "text-red-600" : "text-indigo-600"}`}
              >
                {selectedIds.size} of {maxWords} words selected
              </span>
              {selectedIds.size >= maxWords && (
                <span className="ml-2 text-xs text-red-500">(max reached)</span>
              )}
            </div>
          </div>

          {/* Word Packs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {wordPacks.map((pack) => {
              const isExpanded = expandedPack === pack.id;
              const packSelectedCount = pack.baseWords.filter((bw) =>
                selectedIds.has(bw.id),
              ).length;

              return (
                <div
                  key={pack.id}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                >
                  {/* Pack Header */}
                  <button
                    onClick={() => togglePack(pack.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">
                        {categoryIcons[pack.category] || "📦"}
                      </span>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-800">
                          {pack.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {pack.wordCount} words
                          {packSelectedCount > 0 && (
                            <span className="ml-1 text-indigo-600 font-medium">
                              · {packSelectedCount} selected
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-400 transform transition-transform ${isExpanded ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Expanded Word Grid */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 p-4">
                      <div className="flex justify-between items-center mb-3">
                        <p className="text-sm text-gray-500">
                          {pack.description}
                        </p>
                        <button
                          onClick={() => selectAllFromPack(pack)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap ml-2"
                        >
                          Select All
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {pack.baseWords.map((bw) => {
                          const isSelected = selectedIds.has(bw.id);
                          const translatedText = bw.translatedText;
                          const atLimit =
                            selectedIds.size >= maxWords && !isSelected;

                          return (
                            <button
                              key={bw.id}
                              onClick={() => toggleWord(bw.id)}
                              disabled={atLimit}
                              className={`relative p-3 rounded-lg border-2 text-left transition-all ${
                                isSelected
                                  ? "border-indigo-500 bg-indigo-50"
                                  : atLimit
                                    ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                                    : "border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50"
                              }`}
                            >
                              {/* Image Thumbnail */}
                              {bw.imageUrl && (
                                <div className="mb-2 rounded overflow-hidden aspect-square bg-gray-100">
                                  <img
                                    src={bw.imageUrl}
                                    alt={bw.englishText}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              <p className="text-sm font-semibold text-gray-800">
                                {translatedText || bw.englishText}
                              </p>
                              <p className="text-xs text-gray-500">
                                {bw.englishText}
                              </p>

                              {/* Checkmark */}
                              {isSelected && (
                                <div className="absolute top-1 right-1 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={3}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Create with selected words button */}
          <div className="text-center mb-8">
            <button
              onClick={() => handleSubmit(true)}
              disabled={selectedIds.size === 0 || isSubmitting}
              className={`px-8 py-3 rounded-full font-bold text-lg shadow-lg transition-all ${
                selectedIds.size > 0 && !isSubmitting
                  ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-xl hover:scale-105"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {isSubmitting
                ? "Creating your list..."
                : `Create List with ${selectedIds.size} Words`}
            </button>
          </div>
        </>
      )}

      {/* Community Lists Section */}
      {publicLists.length > 0 && (
        <div className="border-t border-gray-200 pt-6 mt-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
            Community Lists
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {publicLists.map((list) => (
              <a
                key={list.id}
                href={`/spelling-lists/${selectedLanguage}/${list.id}`}
                className="block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-indigo-200 transition"
              >
                <h3 className="font-semibold text-gray-800">{list.name}</h3>
                <p className="text-sm text-gray-500">
                  {list.wordCount} words · {list.languageName}
                </p>
              </a>
            ))}
          </div>
          <div className="text-center">
            <a
              href={browseListsPath}
              className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
            >
              Browse All Community Lists →
            </a>
          </div>
        </div>
      )}

      {/* Upgrade Banner */}
      <div className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg p-4 text-center">
        <p className="text-sm text-gray-700">
          <span className="font-semibold">Free:</span> {maxWords} words per
          week.{" "}
          <a
            href="/pricing"
            className="text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Upgrade for unlimited words + reading adventures →
          </a>
        </p>
      </div>
    </div>
  );
};

SpellingOnboarding.propTypes = {
  language: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  languageInfo: PropTypes.shape({
    flag: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  wordPacks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      description: PropTypes.string,
      category: PropTypes.string,
      wordCount: PropTypes.number,
      baseWords: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          englishText: PropTypes.string.isRequired,
          translatedText: PropTypes.string,
          imageUrl: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  publicLists: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      wordCount: PropTypes.number,
      languageName: PropTypes.string,
    }),
  ),
  maxWords: PropTypes.number.isRequired,
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      flag: PropTypes.string,
      code: PropTypes.string,
    }),
  ).isRequired,
  csrfToken: PropTypes.string.isRequired,
  createPath: PropTypes.string.isRequired,
  browseListsPath: PropTypes.string.isRequired,
};

export default SpellingOnboarding;
